import Datasource, { DatasourceResponse } from "lib/datasource";
import moment from "moment";
import { useEffect, useRef, useState } from "react";
import { SlaStatus, SlaTaskListItem, SlaActiveFilters } from "../types";

// Extends SlaActiveFilters with the per-section slaStatus that is not stored
// in the shared SlaActiveFilters object (api-contract D0.5 / F4).
export interface SlaTaskListFilters extends SlaActiveFilters {
  slaStatus?: SlaStatus | null;
}

interface UseSlaTaskListResult {
  tasks: SlaTaskListItem[];
  isLoading: boolean;
  totalCount: number;
  currentPage: number;
  totalPages: number;
  setPage: (page: number) => void;
  /**
   * The list's own Datasource, so the section can render the SHARED
   * `components/Shared` Pagination — which drives paging by calling
   * `setCurrentPage` / `setPerPage` on the datasource it is given, and reads
   * its `paginationSubject$` for the counts. Handing it out here is what makes
   * the SLA list page like every other paginated list in the app, instead of a
   * lookalike pager wired to `setPage` above.
   */
  datasource: Datasource;
}

export function useSlaTaskList(
  filters: SlaTaskListFilters,
  propertyId?: string
): UseSlaTaskListResult {
  const [tasks, setTasks] = useState<SlaTaskListItem[]>([]);
  const [isLoading, setIsLoading] = useState(false);
  const [totalCount, setTotalCount] = useState(0);
  const [currentPage, setCurrentPage] = useState(1);
  const [totalPages, setTotalPages] = useState(1);
  const [, renderTrigger] = useState(moment());

  const [datasource] = useState(
    () =>
      new Datasource({
        mainModelName: "sla_task",
        perPage: 20,
        currentPage: 1,
        renderTrigger: renderTrigger,
      })
  );

  // Stable stringified representation of filters (excluding the page) so we
  // can detect when the user has changed filter criteria and must reset to page 1.
  const filterKey = [
    filters.dateRangePreset ?? "",
    filters.startDate ?? "",
    filters.endDate ?? "",
    filters.teamMemberIds.join(","),
    filters.slaStatus ?? "",
    filters.taskLifecycleStatus ?? "open",
    propertyId ?? "",
  ].join("|");

  // Keep a ref to the previous filterKey so we can reset the page only when
  // filters (not the page itself) change.
  const prevFilterKeyRef = useRef<string>(filterKey);

  useEffect(() => {
    const sub = datasource.responseSubject$.subscribe((response: DatasourceResponse) => {
      if (response.success) {
        const rows = response.normalizedMainModelResponse ?? [];
        setTasks(Array.isArray(rows) ? rows : [rows]);
        const meta = response.datasourceMeta;
        if (meta) {
          setTotalCount(meta.totalCount);
          setCurrentPage(meta.currentPage);
          setTotalPages(Math.ceil(meta.totalCount / meta.perPage));
        }
      }
    });

    const loadingSub = datasource.loadingSubject$.subscribe((loading) => {
      setIsLoading(loading);
    });

    return () => {
      sub.unsubscribe();
      loadingSub.unsubscribe();
    };
  }, [datasource]);

  useEffect(() => {
    // Reset to page 1 whenever filter criteria change (but not on page changes).
    if (prevFilterKeyRef.current !== filterKey) {
      prevFilterKeyRef.current = filterKey;
      datasource.setCurrentPage(1);
      // setCurrentPage(1) calls touch() which will trigger this effect again
      // via datasource.currentPage — but since filterKey is the same in that
      // second run, we won't loop.
    }

    const params: Record<string, any> = {
      per_page: datasource.perPage,
      page: datasource.currentPage,
    };

    if (filters.dateRangePreset) {
      params.date_range_preset = filters.dateRangePreset;
    } else {
      if (filters.startDate) params.start_date = filters.startDate;
      if (filters.endDate) params.end_date = filters.endDate;
    }

    if (filters.teamMemberIds && filters.teamMemberIds.length > 0) {
      params.team_member_ids = filters.teamMemberIds.join(",");
    }

    if (filters.slaStatus) {
      params.sla_status = filters.slaStatus;
    }

    // api-contract D0.6 / react-frontend-tasks F6 [Corrects breakdown] —
    // "all" is gone; always send the resolved lifecycle (open/completed).
    if (filters.taskLifecycleStatus) {
      params.task_lifecycle_status = filters.taskLifecycleStatus;
    }

    if (propertyId) {
      params.property_id = propertyId;
    }

    datasource.get("v2", "sla_dashboard/tasks", { queryStringParameters: params });
  }, [
    datasource,
    datasource.currentPage,
    // `setPerPage` re-renders via touch() but leaves currentPage alone, so the
    // shared Pagination's "Show N at a time" control would otherwise change the
    // param and never refetch.
    datasource.perPage,
    filterKey,
    filters.dateRangePreset,
    filters.startDate,
    filters.endDate,
    filters.teamMemberIds,
    filters.slaStatus,
    filters.taskLifecycleStatus,
    propertyId,
  ]);

  const setPage = (page: number) => {
    datasource.setCurrentPage(page);
  };

  return { tasks, isLoading, totalCount, currentPage, totalPages, setPage, datasource };
}
